import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  HStack,
  Select,
  VStack,
  Text,
  Divider,
  Link,
  IconButton,
  Flex,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
  Stack,
  Radio,
  RadioGroup,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
} from "@chakra-ui/react";
import { getSupabase, supabaseSchema } from "../components/supabaseClient";
import { dbForRow, foreignSchemaOf, storageSchemaOf } from "../utils/rowSchema";
import {
  prompts,
  tableName,
  submissions_arrayUrls,
  singleLinkSubmissionUrl,
  getBackendURL,
  BASE_URLS,
  bucketName_files,
} from "../components/env";
import {
  generateMedADocsFolderPath,
  generateMedADocsPath,
} from "../documentComponents/documentUtilities";
import PatientSuggestionDropdown from "./PatientSuggestionDropdown";
import { useTemplateOptions } from "../hooks/medstudent/useTemplateOptions";
import { withStaleOption } from "../pages/medstudent/templateCatalog";

import { ExternalLinkIcon, DeleteIcon } from "@chakra-ui/icons";
import { extractAllSubmissionLinks } from "../utils/submissionUtils";
import { scheduleConfirmationForPatient } from "../utils/confirmationScheduling";
import { rowPhones } from "../utils/smsPhone";
import { convertScheduledTime } from "../utils/scheduleTimeDisplay.js";
import { fetchViewerIanaTimezone } from "../utils/scheduleRowTimezone.js";
import { normalizeRole, isInGroup, getLabel, getScheduleField } from "../config/roles";
import { CollaborationDebugToggle } from "./CollaborationDebugPanel";
import { recordingLabel } from "../utils/recordingLabel";

const NOTES_TABLE_CLIENT_ONLY_FIELDS = new Set([
  "displayName",
  "lastVisit",
  "visitCount",
  "timezone_source",
  "owner_name",
  "owner_email",
  "ownerName",
  "ownerEmail",
  "row_owner_name",
  "row_owner_email",
  "_displayTime",
  "_schema", // added by SchedulesTable.js's multi-provider fan-out; not a notes-c column
  "_source", // added by the cross-schema suggestion fan-out (roster rows); not a notes-c column
]);

export function stripNotesTableClientOnlyFields(record = {}) {
  const cleaned = { ...(record || {}) };
  for (const field of NOTES_TABLE_CLIENT_ONLY_FIELDS) {
    delete cleaned[field];
  }
  return cleaned;
}

// Real notes-c columns this modal must never write: the appointment-
// confirmation state belongs to the server (the reply handler, the sender's
// "request sent" stamp, the Status override). The form is seeded from the row
// snapshot the table loaded, so writing them back replaced live values with
// stale ones — a stamp written after the table loaded was wiped (the patient's
// "C" then matched nothing and went to AI chat) and a "C" that arrived meanwhile
// was reverted to U. Series edits spread it onto every sibling.
export const NOTES_TABLE_SERVER_OWNED_FIELDS = new Set([
  "confirmation_status",
  "confirmation_sent_at",
  "confirmation_updated_at",
  "confirmation_source",
  "confirmation_updated_by",
  "confirmation_rules_sent",
]);

export function stripServerOwnedFields(record = {}) {
  const cleaned = { ...(record || {}) };
  for (const field of NOTES_TABLE_SERVER_OWNED_FIELDS) {
    delete cleaned[field];
  }
  return cleaned;
}

// The notes-c column holding an optional per-row template override. Not part
// of `prompts`: that list drives both required-field validation and the generic
// text-input render loop, and this field is optional and renders as a Select.
export const TEMPLATE_FIELD = 'template';

// Columns allowed into this modal's update payload. Derived from `prompts` plus
// the fields that are real notes-c columns but are deliberately kept out of
// prompts. Without `template` here the field is silently dropped on save — the
// UI would look like it worked and the value would never persist.
export function buildUpdateFieldWhitelist() {
  return [...prompts.map(([key]) => key), TEMPLATE_FIELD];
}

const EditPatientModal = ({
  isOpen,
  onClose,
  patientData,
  onUpdatePatient,
  isDuplicate,
  userRole,
  /** When true (e.g. shared as viewer/editor): schedule fields cannot be edited; document may still be editable elsewhere. */
  readOnlyScheduleFields = false,
}) => {
  const nameFieldKey = prompts[0][0];

  // Cross-schema rows — the search page and the Providers fan-out merge results
  // from every linked provider schema and tag each row with `_schema`. Every
  // table read/write below has to aim at THAT schema: getSupabase() is bound to
  // the viewer's own, so `.eq('id', <foreign uuid>)` matches nothing there and
  // an update returns `200 []` with no error — the save silently does nothing.
  // Falsy / home `_schema` keeps the previous single-schema behaviour exactly.
  const patientSchema = foreignSchemaOf(patientData, supabaseSchema);
  /** Schema-scoped table client for this patient's row (storage/auth stay on `client`). */
  const tablesFor = (client) => dbForRow(client, patientData, supabaseSchema);
  /** Schema that owns this patient's storage paths (never null). */
  const pathSchema = storageSchemaOf(patientData, supabaseSchema);

  const normalizeToQuarterHour24h = (timeValue) => {
    if (!timeValue || typeof timeValue !== "string") {
      return "00:00:00";
    }
    const t = timeValue.trim();
    const ampmMatch = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    let h;
    let m;
    if (ampmMatch) {
      h = parseInt(ampmMatch[1], 10);
      m = parseInt(ampmMatch[2], 10);
      const period = ampmMatch[3].toUpperCase();
      if (period === "PM" && h !== 12) h += 12;
      if (period === "AM" && h === 12) h = 0;
    } else {
      const h24Match = t.match(/^(\d{1,2}):(\d{2})/);
      if (!h24Match) return "00:00:00";
      h = parseInt(h24Match[1], 10);
      m = parseInt(h24Match[2], 10);
    }
    let total = h * 60 + m;
    total = Math.round(total / 15) * 15;
    total = ((total % 1440) + 1440) % 1440;
    const outH = String(Math.floor(total / 60)).padStart(2, "0");
    const outM = String(total % 60).padStart(2, "0");
    return `${outH}:${outM}:00`;
  };

  const toTimeComponents = (time24h) => {
    const [hours = "00", minutes = "00"] = String(time24h).split(":");
    const hh = parseInt(hours, 10) || 0;
    return {
      hours: String(hh % 12 || 12).padStart(2, "0"),
      minutes: minutes || "00",
      ampm: hh >= 12 ? "PM" : "AM",
    };
  };
  // Stored time_ is the row owner's wall clock; the schedules table converts it
  // to the viewer's timezone for display (convertScheduledTime). Mirror that here
  // so the time picker shows — and saves — the same value the table shows.
  // No-op when ownerTz is missing or equals the viewer's zone.
  const ownerTimeToViewer = (time_, date_, ownerTz, viewerTz) => {
    if (!time_) return "00:00:00";
    const converted = convertScheduledTime(date_, time_, ownerTz, viewerTz);
    if (!converted || converted === "N/A") {
      return normalizeToQuarterHour24h(time_);
    }
    return `${converted}:00`;
  };
  // Inverse of ownerTimeToViewer: take the viewer-timezone picker value and
  // express it as the owner's wall clock for storage in notes-c.
  const viewerTimeToOwner = (time_, date_, viewerTz, ownerTz) => {
    if (!time_) return "00:00:00";
    const converted = convertScheduledTime(date_, time_, viewerTz, ownerTz);
    if (!converted || converted === "N/A") {
      return normalizeToQuarterHour24h(time_);
    }
    return `${converted}:00`;
  };
  const [formData, setFormData] = useState({});
  const [timeComponents, setTimeComponents] = useState({
    hours: "12",
    minutes: "00",
    ampm: "AM",
  });
  const [viewerTimezone, setViewerTimezone] = useState("America/New_York");
  const [rowOwner, setRowOwner] = useState(null);
  const [rowOwnerLoading, setRowOwnerLoading] = useState(false);
  const [nameComponents, setNameComponents] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
  });
  const [loading, setLoading] = useState(false);

  // Add state for patient suggestion
  const [suggestedPatientName, setSuggestedPatientName] = useState("");

  // Submission links (doctors only - hidden for students/social workers)
  const [submissionLinks, setSubmissionLinks] = useState([]);
  const [audioList, setAudioList] = useState([]);
  const toast = useToast();
  const [audioUploading, setAudioUploading] = useState(false);
  const audioInputRef = useRef(null);
  const [transcriptList, setTranscriptList] = useState([]);
  const [transcriptUploading, setTranscriptUploading] = useState(false);
  const transcriptInputRef = useRef(null);

  // Recurring schedules (freemium only) - same as Add modal
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringWeeks, setRecurringWeeks] = useState(1);
  const [recurringDays, setRecurringDays] = useState([]);
  const [notesBehavior, setNotesBehavior] = useState("accumulate");
  // FFP/FFS are always offered here — see AddPatientModal for the reasoning.
  const { options: templateOptions } = useTemplateOptions({ includeFollowUps: true });

  const preloadedRowOwner = (() => {
    const name =
      patientData?.owner_name ||
      patientData?.ownerName ||
      patientData?.row_owner_name ||
      "";
    const email =
      patientData?.owner_email ||
      patientData?.ownerEmail ||
      patientData?.row_owner_email ||
      "";
    return {
      id: patientData?.user_id || "",
      name: String(name || "").trim(),
      email: String(email || "").trim(),
    };
  })();
  const hasPreloadedRowOwner =
    Boolean(preloadedRowOwner.name) || Boolean(preloadedRowOwner.email);

  const toggleRecurringDay = (day) => {
    formDirtyRef.current = true;
    recurringOptionsDirtyRef.current = true;
    changedFieldsRef.current.add("recurring_days");
    setRecurringDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  /** EHR_ID for the series as when modal opened; used to find series rows on save. */
  const originalSeriesEhrRef = useRef("");
  /** Appointment date when the modal opened (YYYY-MM-DD); used for series date-shift prompts. */
  const originalDateRef = useRef("");
  /** 'single' = only this row; 'series' = other rows in EHR series too. Cleared after save. */
  const seriesScopeRef = useRef(null);
  /** Weekdays (0–6) to apply the calendar date delta to when updating siblings; null = no sibling date moves. */
  const weekdayShiftMaskRef = useRef(null);
  const formDirtyRef = useRef(false);
  const changedFieldsRef = useRef(new Set());
  const recurringOptionsDirtyRef = useRef(false);
  const [seriesRows, setSeriesRows] = useState([]);
  const {
    isOpen: isSeriesScopeOpen,
    onOpen: onSeriesScopeOpen,
    onClose: onSeriesScopeClose,
  } = useDisclosure();
  const cancelSeriesScopeRef = useRef(null);
  const {
    isOpen: isWeekdayShiftOpen,
    onOpen: onWeekdayShiftOpen,
    onClose: onWeekdayShiftClose,
  } = useDisclosure();
  const cancelWeekdayShiftRef = useRef(null);
  const prevWeekdayShiftOpenRef = useRef(false);
  const [selectedWeekdaysForShift, setSelectedWeekdaysForShift] = useState([]);
  const calendarDeltaDays = (fromYmd, toYmd) => {
    const parse = (s) => {
      const [y, m, d] = String(s || "").split("-").map(Number);
      return y && m && d ? new Date(y, m - 1, d) : null;
    };
    const a = parse(fromYmd);
    const b = parse(toYmd);
    if (!a || !b) return 0;
    return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
  };

  const addCalendarDaysToYmd = (ymd, deltaDays) => {
    const [y, m, d] = String(ymd || "").split("-").map(Number);
    if (!y || !m || !d) return ymd;
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + deltaDays);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };

  const getDayOfWeekFromYmd = (ymd) => {
    const [y, m, d] = String(ymd || "").split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d).getDay();
  };

  /** Normalize EHR_ID for equality (handles "123" vs 123 vs "0123"). */
  const normalizeEhrIdForMatch = (v) => {
    const s = String(v ?? "").trim();
    if (!s) return "";
    if (/^\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      if (Number.isFinite(n)) return String(Math.trunc(n));
    }
    return s;
  };

  const rowMatchesSeriesEhr = (row, seriesEhrId) => {
    const want = normalizeEhrIdForMatch(seriesEhrId);
    if (!want) return false;
    return normalizeEhrIdForMatch(row?.EHR_ID) === want;
  };

  /** Keep only rows whose EHR_ID matches the series (after fetch / string+numeric merge). */
  const filterRowsByMatchingEhr = (rows, seriesEhrId) =>
    (rows || []).filter((r) => rowMatchesSeriesEhr(r, seriesEhrId));

  /** ISO YYYY-MM-DD string compare; invalid dates fall back to 0. */
  const compareYmd = (a, b) => {
    const as = String(a || "").trim();
    const bs = String(b || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(as) || !/^\d{4}-\d{2}-\d{2}$/.test(bs)) {
      return 0;
    }
    return as < bs ? -1 : as > bs ? 1 : 0;
  };

  /** Rows with date_ on or after anchor (same calendar day as the edited occurrence counts). Past-only rows excluded. */
  const filterSeriesRowsOnOrAfter = (rows, anchorYmd) => {
    const a = String(anchorYmd || "").trim();
    if (!a || !/^\d{4}-\d{2}-\d{2}$/.test(a)) return rows || [];
    return (rows || []).filter((r) => {
      const ry = String(r?.date_ || "").trim();
      return ry && /^\d{4}-\d{2}-\d{2}$/.test(ry) && compareYmd(ry, a) >= 0;
    });
  };

  /** Local calendar YYYY-MM-DD — used to list weekdays still upcoming in the series (vs. anchor = edited date). */
  const getLocalTodayYmd = () => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  };

  const weekdaysInSeriesFromRows = (rows) => {
    const seen = new Set();
    (rows || []).forEach((r) => {
      const w = getDayOfWeekFromYmd(r?.date_);
      if (w != null) seen.add(w);
    });
    return [...seen].sort((a, b) => a - b);
  };

  /** After moving a given weekday by delta calendar days, resulting weekday (0–6). */
  const weekdayAfterCalendarDelta = (weekday, deltaDays) => {
    const t = new Date(2024, 0, 7 + weekday);
    t.setDate(t.getDate() + deltaDays);
    return t.getDay();
  };

  // `db` must already be scoped to the patient's schema (see tablesFor).
  const fetchRecurringSeriesRows = async (
    db,
    ehrId,
    columns = "id, date_, time_, EHR_ID",
  ) => {
    const trimmedEhrId = ehrId != null ? String(ehrId).trim() : "";
    if (!trimmedEhrId) {
      return { data: [], error: null };
    }
    const mergeById = (lists) => {
      const map = new Map();
      lists.flat().forEach((r) => {
        if (r && r.id != null) map.set(r.id, r);
      });
      return [...map.values()];
    };
    const { data: asString, error: errString } = await db
      .from(tableName)
      .select(columns)
      .eq("EHR_ID", trimmedEhrId);
    if (errString) return { data: [], error: errString };
    let rows = mergeById([asString || []]);
    const numeric =
      /^\d+$/.test(trimmedEhrId) ? Number(trimmedEhrId) : null;
    if (numeric != null && Number.isFinite(numeric)) {
      const { data: asNum, error: errNum } = await db
        .from(tableName)
        .select(columns)
        .eq("EHR_ID", numeric);
      if (!errNum && asNum?.length) {
        rows = mergeById([rows, asNum]);
      }
    }
    rows = filterRowsByMatchingEhr(rows, trimmedEhrId);
    return { data: rows, error: null };
  };

  const markFieldDirty = (fieldKey) => {
    formDirtyRef.current = true;
    if (fieldKey) {
      changedFieldsRef.current.add(fieldKey);
    }
  };

  const hasPromptRelevantChanges = () => {
    // "Add more meetings" / day checkboxes only — no "this vs series" prompt (new rows, not bulk edit).
    // Date changes DO prompt when multiple rows share this EHR (apply to this occurrence vs series).
    const ignored = new Set(["recurring_days"]);
    for (const key of changedFieldsRef.current) {
      if (!ignored.has(key)) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset suggestion state when modal closes
      setSuggestedPatientName("");
      setSubmissionLinks([]);
      setAudioList([]);
      setTranscriptList([]);
      setSeriesRows([]);
      setIsRecurring(false);
      setRecurringWeeks(1);
      setRecurringDays([]);
      setNotesBehavior("accumulate");
      setRowOwner(null);
      setRowOwnerLoading(false);
      originalSeriesEhrRef.current = "";
      originalDateRef.current = "";
      seriesScopeRef.current = null;
      weekdayShiftMaskRef.current = null;
      formDirtyRef.current = false;
      changedFieldsRef.current = new Set();
      recurringOptionsDirtyRef.current = false;
      return;
    }

    // Debug: Log the patient data to see what's being loaded
    console.log("🔍 EditPatientModal: Patient data loaded:", patientData);
    console.log("🔍 EditPatientModal: DOB field value:", patientData?.dob);

    if (!patientData) {
      setNameComponents({ firstName: "", middleName: "", lastName: "" });
      setTimeComponents({ hours: "12", minutes: "00", ampm: "AM" });
      return;
    }

    // Process name field
    const fullName = patientData[nameFieldKey] || "";
    let lastName = "",
      firstName = "",
      middleName = "";

    if (fullName.includes(",")) {
      const [lastPart, firstMiddlePart] = fullName
        .split(",")
        .map((s) => s.trim());
      const [first, ...middle] = firstMiddlePart
        ? firstMiddlePart.split(" ")
        : [];
      lastName = lastPart;
      firstName = first || "";
      middleName = middle.join(" ") || "";
    } else {
      const nameParts = fullName.split(" ");
      lastName = nameParts.pop() || "";
      firstName = nameParts.shift() || "";
      middleName = nameParts.join(" ") || "";
    }
    setNameComponents({ firstName, middleName, lastName });

    // Time picker is populated by the timezone-aware effect below; seed formData
    // with the raw normalized value so it's defined before conversion runs.
    originalDateRef.current = String(patientData.date_ || "").trim();
    setFormData({
      ...(patientData || {}),
      time_: normalizeToQuarterHour24h(patientData.time_),
      notes_behavior:
        patientData.notes_behavior === "fresh" ? "fresh" : "accumulate",
    });
    setNotesBehavior(
      patientData.notes_behavior === "fresh" ? "fresh" : "accumulate",
    );
    setRecurringDays([]);
    // Fetch submission links for doctors (students/social workers don't see this field)
    const fetchLinks = async () => {
      if (patientData?.id) {
        const links = await extractAllSubmissionLinks(
          patientData.id,
          patientSchema || undefined,
        );
        setSubmissionLinks(links);
      }
    };
    fetchLinks();

    // Re-read `template` straight from the row when the modal opens.
    //
    // `patientData` comes from the Schedules list, which is fetched once and
    // has no idea the clinic page's Reload Template button just wrote a new
    // value to this row. Without this the field shows a stale "Use type" even
    // though the row already carries a template. Scoped to this one column so
    // nothing else the user may have edited in the list gets clobbered.
    const refreshTemplate = async () => {
      if (!patientData?.id) return;
      try {
        const sb = await getSupabase();
        const { data, error } = await sb
          .schema(patientSchema || supabaseSchema || 'public')
          .from(tableName)
          .select('template')
          .eq('id', patientData.id)
          .single();
        if (error || !data) return;
        setFormData((prev) => ({ ...prev, template: data.template || '' }));
      } catch (_) {
        // Non-fatal — fall back to whatever the list handed us.
      }
    };
    refreshTemplate();

    // Populate audio list from more_audios JSONB array, generating signed playback URLs
    const rawAudios = Array.isArray(patientData?.more_audios) ? patientData.more_audios : [];
    setAudioList(rawAudios);
    // Populate transcript list from transcripts JSONB array
    const rawTranscripts = Array.isArray(patientData?.transcripts) ? patientData.transcripts : [];
    setTranscriptList(rawTranscripts);
    if (rawAudios.length > 0) {
      (async () => {
        try {
          const supabase = await getSupabase();
          const { bucketName: audioBucket } = await import('../components/env');
          const withSigned = await Promise.all(rawAudios.map(async (a) => {
            if (!a.storagePath) return a;
            const { data } = await supabase.storage.from(audioBucket).createSignedUrl(a.storagePath, 3600);
            return data?.signedUrl ? { ...a, playbackUrl: data.signedUrl } : a;
          }));
          setAudioList(withSigned);
        } catch (e) {
          console.error('Failed to generate signed audio URLs', e);
        }
      })();
    }
    setIsRecurring(false);
    setRecurringWeeks(1);
    formDirtyRef.current = false;
    changedFieldsRef.current = new Set();
    recurringOptionsDirtyRef.current = false;
  }, [isOpen, patientData]);

  // Resolve the viewer's IANA timezone once per open (same source the table uses).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const supabase = await getSupabase();
      const vtz = await fetchViewerIanaTimezone(supabase);
      if (!cancelled && vtz) setViewerTimezone(vtz);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Display the schedule row owner for shared rows without making ownership editable.
  useEffect(() => {
    if (!isOpen) return;
    const ownerId = patientData?.user_id;
    if (hasPreloadedRowOwner) {
      setRowOwner(preloadedRowOwner);
      setRowOwnerLoading(false);
      return;
    }
    if (!ownerId) {
      setRowOwner(null);
      setRowOwnerLoading(false);
      return;
    }
    let cancelled = false;
    setRowOwner(null);
    setRowOwnerLoading(true);
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data, error } = await tablesFor(supabase)
          .from("users")
          .select("id, name, email")
          .eq("id", ownerId)
          .maybeSingle();
        const hasDisplayName =
          data?.name && String(data.name).trim();
        const hasDisplayEmail =
          data?.email && String(data.email).trim();
        if (!error && (hasDisplayName || hasDisplayEmail)) {
          if (!cancelled) {
            setRowOwner(data || null);
            setRowOwnerLoading(false);
          }
          return;
        }
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const viewerId = session?.user?.id;
        if (viewerId) {
          const res = await fetch(
            `${getBackendURL()}/api/schema-users/?user_id=${encodeURIComponent(viewerId)}`,
          );
          const json = await res.json().catch(() => ({}));
          const enrichedOwner = Array.isArray(json.users)
            ? json.users.find(
                (user) =>
                  String(user?.id || "").toLowerCase() ===
                  String(ownerId).toLowerCase(),
              )
            : null;
          if (
            enrichedOwner &&
            ((enrichedOwner.name && String(enrichedOwner.name).trim()) ||
              (enrichedOwner.email && String(enrichedOwner.email).trim()))
          ) {
            if (!cancelled) {
              setRowOwner(enrichedOwner);
              setRowOwnerLoading(false);
            }
            return;
          }
        }
        if (!cancelled) {
          setRowOwner(error ? null : data || null);
          setRowOwnerLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("EditPatientModal: row owner lookup failed:", error);
          setRowOwner(null);
          setRowOwnerLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    patientData?.user_id,
    patientData?.owner_name,
    patientData?.owner_email,
    patientData?.ownerName,
    patientData?.ownerEmail,
    patientData?.row_owner_name,
    patientData?.row_owner_email,
    hasPreloadedRowOwner,
  ]);

  // Populate the time picker in the viewer's timezone (matching SchedulesTable).
  // Derived from the raw stored time_, so re-running when viewerTimezone resolves
  // is idempotent. Skip once the user has edited the time so a late timezone
  // resolution can't overwrite their pick (save still uses the resolved zone).
  useEffect(() => {
    if (!isOpen || !patientData) return;
    if (changedFieldsRef.current.has("time_")) return;
    const viewerTime24 = ownerTimeToViewer(
      patientData.time_,
      patientData.date_,
      patientData.timezone_source || null,
      viewerTimezone,
    );
    const normalized = normalizeToQuarterHour24h(viewerTime24);
    setTimeComponents(toTimeComponents(normalized));
    setFormData((prev) => ({ ...prev, time_: normalized }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patientData, viewerTimezone]);

  // Rows with same EHR_ID (series semantics for edits)
  useEffect(() => {
    if (!isOpen || !patientData?.id || isDuplicate) {
      setSeriesRows([]);
      return;
    }
    let cancelled = false;
    const seriesEhrId = String(patientData.EHR_ID ?? "").trim();
    originalSeriesEhrRef.current = seriesEhrId;
    if (!seriesEhrId) {
      setSeriesRows([]);
      return;
    }
    (async () => {
      const supabase = await getSupabase();
      const { data, error } = await fetchRecurringSeriesRows(
        tablesFor(supabase),
        seriesEhrId,
        "id, date_, time_, EHR_ID",
      );
      if (cancelled) return;
      if (error) {
        console.warn("EditPatientModal: recurring series fetch failed:", error);
        setSeriesRows([]);
        return;
      }
      const rows = Array.isArray(data) ? data : [];
      setSeriesRows(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, patientData, isDuplicate, nameFieldKey]);

  const handleChange = (key, value) => {
    markFieldDirty(key);
    // Ensure date format is correct for date inputs
    if (key === "dob" && value) {
      // Convert to YYYY-MM-DD format if needed
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        value = date.toISOString().split("T")[0];
      }
    }
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleTimeChange = (type, value) => {
    markFieldDirty("time_");
    const updatedTime = { ...timeComponents, [type]: value };
    setTimeComponents(updatedTime);
    // Store in 24h format so the appointment scheduler can parse it reliably
    let h = parseInt(updatedTime.hours, 10);
    if (updatedTime.ampm === "PM" && h !== 12) h += 12;
    if (updatedTime.ampm === "AM" && h === 12) h = 0;
    handleChange("time_", `${String(h).padStart(2, "0")}:${updatedTime.minutes}:00`);
  };

  const handleTimeInputChange = (value) => {
    markFieldDirty("time_");
    const normalized = normalizeToQuarterHour24h(value);
    setTimeComponents(toTimeComponents(normalized));
    handleChange("time_", normalized);
  };

  const handleNameChange = (type, value) => {
    markFieldDirty(nameFieldKey);
    const names = { ...nameComponents, [type]: value };
    setNameComponents(names);

    const fullName = [
      names.lastName,
      [names.firstName, names.middleName].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");

    handleChange(nameFieldKey, fullName);
  };

  const handleLinkChange = (index, value) => {
    markFieldDirty("submissions_array");
    const updatedLinks = [...submissionLinks];
    updatedLinks[index] = value;
    setSubmissionLinks(updatedLinks);
  };
  const handleDeleteLink = (index) => {
    markFieldDirty("submissions_array");
    setSubmissionLinks(submissionLinks.filter((_, i) => i !== index));
  };
  const handleAddLink = () => {
    markFieldDirty("submissions_array");
    setSubmissionLinks([...submissionLinks, ""]);
  };

  const handleDeleteAudio = async (index) => {
    const audio = audioList[index];
    const updated = audioList.filter((_, i) => i !== index);
    setAudioList(updated);
    try {
      const supabase = await getSupabase();
      await tablesFor(supabase).from(tableName).update({ more_audios: updated }).eq('id', patientData.id);
      // Best-effort remove from storage (non-blocking)
      if (audio?.storagePath) {
        const { bucketName: audioBucket } = await import('../components/env');
        supabase.storage.from(audioBucket).remove([audio.storagePath]).catch(() => {});
      }
    } catch (e) {
      console.error('handleDeleteAudio failed:', e);
    }
  };

  const handleUploadAudio = async (file) => {
    if (!file || !patientData?.id) return;
    setAudioUploading(true);
    try {
      const supabase = await getSupabase();
      const { bucketName: audioBucket, supabaseUrl: sbUrl } = await import('../components/env');
      const ext = (file.name.split('.').pop() || 'webm').toLowerCase();
      const EXT_TYPES = { webm: 'audio/webm', mp3: 'audio/mpeg', mp4: 'audio/mp4', m4a: 'audio/mp4', ogg: 'audio/ogg', wav: 'audio/wav', aac: 'audio/aac' };
      const contentType = file.type || EXT_TYPES[ext] || 'audio/webm';
      const baseName = file.name.replace(new RegExp(`\\.${ext}$`, 'i'), '');
      const safeName = `${baseName.replace(/[^a-zA-Z0-9._-]/g, '_')}_${Date.now()}.${ext}`;
      const now = new Date();
      const date_ = patientData.date_ || now.toISOString().split('T')[0];
      const year = date_.split('-')[0];
      const storagePath = `${pathSchema}/${year}/${date_}/${safeName}`;
      const { error: upErr } = await supabase.storage.from(audioBucket).upload(storagePath, file, { contentType });
      if (upErr) {
        console.error('Storage upload error:', JSON.stringify(upErr));
        throw upErr;
      }
      const url = `${sbUrl}/storage/v1/object/public/${audioBucket}/${storagePath}`;
      // Generate signed URL for immediate playback (bucket may be private)
      const { data: signedData } = await supabase.storage.from(audioBucket).createSignedUrl(storagePath, 3600);
      const newAudio = {
        name: file.name,
        displayName: file.name,
        url,
        storagePath,
        playbackUrl: signedData?.signedUrl || url,
        uploaded_at: now.toISOString(),
        source: 'uploaded',
        type: 'audio',
        patient_id: patientData.id,
        file_size: file.size,
        content_type: contentType,
      };
      const updated = [...audioList, newAudio];
      setAudioList(updated);
      const { error: dbErr } = await tablesFor(supabase).from(tableName).update({ more_audios: updated }).eq('id', patientData.id);
      if (dbErr) {
        console.error('DB update after upload failed:', JSON.stringify(dbErr));
        toast({ title: 'Upload failed to save', status: 'error', duration: 3000, isClosable: true });
      } else {
        toast({ title: 'Audio uploaded successfully', status: 'success', duration: 2500, isClosable: true });
        if (onUpdatePatient) onUpdatePatient({ ...patientData, more_audios: updated });
      }
    } catch (e) {
      console.error('handleUploadAudio failed:', e?.message || e, e?.statusCode, e?.error);
    } finally {
      setAudioUploading(false);
    }
  };

  const handleUploadTranscript = async (file) => {
    if (!file || !patientData?.id) return;
    setTranscriptUploading(true);
    try {
      const text = await file.text();
      const supabase = await getSupabase();
      const newEntry = {
        id: Date.now().toString(),
        patient_id: patientData.id?.toString() || '',
        name: patientData[nameFieldKey] || '',
        transcript: [text],
        facts: [],
        soap_notes: {},
        audio_file_name: file.name,
        audio_source: 'uploaded_transcript',
        scheduled_date: patientData.date_ || null,
        date_of_service: patientData.date_ || null,
        created_at: new Date().toISOString(),
      };
      const updated = [...transcriptList, newEntry];
      const { error: dbErr } = await tablesFor(supabase).from(tableName).update({ transcripts: updated }).eq('id', patientData.id);
      if (dbErr) {
        console.error('Transcript DB update failed:', JSON.stringify(dbErr));
        toast({ title: 'Transcript upload failed', status: 'error', duration: 3000, isClosable: true });
      } else {
        setTranscriptList(updated);
        toast({ title: 'Transcript uploaded successfully', status: 'success', duration: 2500, isClosable: true });
        if (onUpdatePatient) onUpdatePatient({ ...patientData, transcripts: updated });
      }
    } catch (e) {
      console.error('handleUploadTranscript failed:', e?.message || e);
      toast({ title: 'Transcript upload failed', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setTranscriptUploading(false);
    }
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (selectedPatient) => {
    markFieldDirty("patient_suggestion_copy");
    console.log(
      "📅 EditPatientModal: Patient selected from dropdown:",
      selectedPatient,
    );

    // Parse the selected patient's name
    const fullName =
      selectedPatient.displayName || selectedPatient[nameFieldKey];
    let lastName = "",
      firstName = "",
      middleName = "";

    if (fullName.includes(",")) {
      const [lastPart, firstMiddlePart] = fullName
        .split(",")
        .map((s) => s.trim());
      const [first, ...middle] = firstMiddlePart
        ? firstMiddlePart.split(" ")
        : [];
      lastName = lastPart;
      firstName = first || "";
      middleName = middle.join(" ") || "";
    } else {
      const nameParts = fullName.split(" ");
      lastName = nameParts.pop() || "";
      firstName = nameParts.shift() || "";
      middleName = nameParts.join(" ") || "";
    }

    // Update name components
    setNameComponents({ firstName, middleName, lastName });

    // Update form data with selected patient's information, but preserve the current patient's ID and date
    const {
      more_audios,
      created_at,
      id,
      transcripts,
      date_,
      ...patientDataWithoutAudio
    } = selectedPatient;
    // Show the suggested patient's time in the viewer's timezone, like the picker.
    const normalizedSelectedTime24 = normalizeToQuarterHour24h(
      ownerTimeToViewer(
        selectedPatient.time_,
        formData.date_ || patientData?.date_,
        selectedPatient.timezone_source || patientData?.timezone_source || null,
        viewerTimezone,
      ),
    );
    setFormData((prev) => ({
      ...prev,
      ...patientDataWithoutAudio,
      // Preserve the current patient's ID and date
      id: prev.id,
      date_: prev.date_,
      time_: normalizedSelectedTime24,
    }));

    setTimeComponents(toTimeComponents(normalizedSelectedTime24));

    // Clear the suggestion input
    setSuggestedPatientName("");
  };

  // Handle patient name suggestion input
  const handlePatientNameSuggestion = (value) => {
    setSuggestedPatientName(value);
  };

  // Explain common Supabase/Postgres errors so the console shows why the save failed
  const explainSaveError = (err, context = "save") => {
    if (!err) return "";
    const code = err.code;
    const msg = (err.message || "").toLowerCase();
    const parts = [`[EditPatientModal] ${context} failed: ${err.message || err}`];
    if (code === "22P02") {
      parts.push(
        "Why: Postgres rejected a value for a typed column (e.g. UUID, number, date). " +
          "Common causes: empty string \"\" or \"[]\" sent to a UUID column; invalid date format; or non-numeric text in a numeric column. " +
          "Check the payload logged above for empty strings or arrays on UUID/date/numeric fields and use null instead."
      );
    } else if (msg.includes("null value") && msg.includes("violates")) {
      parts.push(
        "Why: A required (NOT NULL) column was sent null or is missing. Check the payload for required fields."
      );
    } else if (msg.includes("duplicate key") || msg.includes("unique constraint")) {
      parts.push("Why: Another row already has the same value for a unique column. Change the conflicting field or remove the duplicate.");
    } else if (err.status === 400 || err.statusCode === 400) {
      parts.push(
        "Why: Bad request — server rejected the payload (often type mismatch or invalid value). See error message and payload above."
      );
    }
    return parts.join(" ");
  };

  // Move report files from old date folder to new date folder when date changes
  const moveReportsToNewDate = async (supabase, oldDate, newDate, reportsPath) => {
    if (!reportsPath || !oldDate || !newDate || oldDate === newDate) return null;

    // Convert dates from YYYY-MM-DD to MM-DD-YY format for folder paths
    const formatDateForPath = (dateStr) => {
      const [year, month, day] = dateStr.split("-");
      return `${month}-${day}-${year.slice(2)}`;
    };

    const oldDateFolder = formatDateForPath(oldDate);
    const newDateFolder = formatDateForPath(newDate);

    // Parse the reports path to find the base (patient folder) and date folder
    const pathParts = reportsPath.split("/");
    let basePath = reportsPath;
    const lastPart = pathParts[pathParts.length - 1];

    // If the last part is a date folder (MM-DD-YY), use everything before it as base
    if (/^\d{2}-\d{2}-\d{2}$/.test(lastPart)) {
      basePath = pathParts.slice(0, -1).join("/");
    }

    const oldFolderPath = `${basePath}/${oldDateFolder}`;
    const newFolderPath = `${basePath}/${newDateFolder}`;

    console.log(`📁 Moving reports from ${oldFolderPath} to ${newFolderPath}`);

    try {
      // List all files in the old date folder
      const { data: files, error: listError } = await supabase.storage
        .from(bucketName_files)
        .list(oldFolderPath);

      if (listError) {
        console.error("Error listing files in old date folder:", listError);
        return null;
      }

      if (!files || files.length === 0) {
        console.log("No files found in old date folder, skipping move");
        return newFolderPath;
      }

      const filesToMove = files.filter((item) => item.name.includes("."));
      console.log(`📁 Found ${filesToMove.length} files to move`);

      // Move each file to the new date folder and update file_classification_cache
      for (const file of filesToMove) {
        const oldFilePath = `${oldFolderPath}/${file.name}`;
        const newFilePath = `${newFolderPath}/${file.name}`;

        console.log(`📁 Moving: ${oldFilePath} -> ${newFilePath}`);
        const { error: moveError } = await supabase.storage
          .from(bucketName_files)
          .move(oldFilePath, newFilePath);

        if (moveError) {
          console.error(`Error moving file ${file.name}:`, moveError);
        } else {
          // Update file_classification_cache entry to reflect the new path
          const { error: cacheUpdateError } = await tablesFor(supabase)
            .from("file_classification_cache")
            .update({ file_path: newFilePath })
            .eq("file_path", oldFilePath);

          if (cacheUpdateError) {
            console.error(`Error updating cache for ${file.name}:`, cacheUpdateError);
          } else {
            console.log(`📁 Updated cache path: ${oldFilePath} -> ${newFilePath}`);
          }
        }
      }

      console.log(`✅ Successfully moved ${filesToMove.length} files to ${newFolderPath}`);
      return newFolderPath;
    } catch (error) {
      console.error("Error moving reports:", error);
      return null;
    }
  };

  // Move Med-A docs from old date folder to new date folder when date changes (tenant + doctor)
  // Med-a doc filename format: {patientName}-{EHR_ID}.json (e.g. "Shahwan, Omar-123456.json")
  const moveMedADocsToNewDate = async (supabase, oldDate, newDate, ehrId, patientName) => {
    if (!oldDate || !newDate || oldDate === newDate) return;
    const schema = pathSchema;
    const [oldY] = oldDate.split("-");
    const [newY] = newDate.split("-");
    const oldFolder = generateMedADocsFolderPath(oldY, oldDate, schema);
    const newFolder = generateMedADocsFolderPath(newY, newDate, schema);
    try {
      const { data: files, error: listError } = await supabase.storage
        .from(bucketName_files)
        .list(oldFolder);
      if (listError || !files?.length) return;
      const ehrIdStr = String(ehrId ?? "").trim();
      const toMove = files.filter((item) => {
        if (!item.name || !item.name.endsWith(".json")) return false;
        // Match by EHR_ID in filename (e.g. "Shahwan, Omar-123456.json")
        if (ehrIdStr && (item.name.endsWith(`-${ehrIdStr}.json`) || item.name.includes(`-${ehrIdStr}.json`)))
          return true;
        // Match by patient name prefix (e.g. "Shahwan, Omar-...")
        if (patientName && item.name.startsWith(`${patientName}-`)) return true;
        return false;
      });
      for (const file of toMove) {
        if (!file.name || file.name.includes(".") === false) continue;
        const oldPath = `${oldFolder}${file.name}`;
        const newPath = `${newFolder}${file.name}`;
        const { error: moveError } = await supabase.storage.from(bucketName_files).move(oldPath, newPath);
        if (!moveError) {
          const { error: cacheUpdateError } = await tablesFor(supabase)
            .from("file_classification_cache")
            .update({ file_path: newPath })
            .eq("file_path", oldPath);
          if (cacheUpdateError) {
            console.error(`Error updating cache for Med-A doc ${file.name}:`, cacheUpdateError);
          } else {
            console.log(`📁 Updated cache path: ${oldPath} -> ${newPath}`);
          }
        }
      }
      if (toMove.length) console.log(`✅ Moved ${toMove.length} Med-A doc(s) to new date folder`);
    } catch (err) {
      console.warn("Error moving Med-A docs to new date:", err);
    }
  };

  // Move this meeting's Med-A doc from old path to new path when name, id, or date change,
  // so the same doc stays attached to the meeting instead of being left at the old path.
  const moveMedADocToNewPath = async (supabase, patientData, formData) => {
    const oldName = (patientData[nameFieldKey] || "").trim();
    const oldId = String(patientData.EHR_ID ?? patientData.email ?? "").trim();
    const oldDate = patientData.date_ || "";
    const newName = (formData[nameFieldKey] || "").trim();
    const newId = String(formData.EHR_ID ?? formData.email ?? "").trim();
    const newDate = formData.date_ || "";
    if (!oldDate || !newDate) return;
    const oldFileName = `${oldName}-${oldId}.json`;
    const newFileName = `${newName}-${newId}.json`;
    if (oldFileName === newFileName && oldDate === newDate) return;
    const [oldYear] = oldDate.split("-");
    const [newYear] = newDate.split("-");
    const oldPath = await generateMedADocsPath(oldYear, oldDate, oldFileName, pathSchema);
    const newPath = await generateMedADocsPath(newYear, newDate, newFileName, pathSchema);
    if (oldPath === newPath) return;
    try {
      // Check if document exists before attempting move (avoids 400 when source is missing)
      const folderPath = oldPath.substring(0, oldPath.lastIndexOf("/"));
      const { data: folderContents } = await supabase.storage
        .from(bucketName_files)
        .list(folderPath);
      const exists = folderContents?.some((f) => f.name === oldFileName);
      if (!exists) {
        console.warn("📄 [Med-A doc] No existing doc at old path, skipping move:", oldPath);
        return;
      }
      const { error: moveError } = await supabase.storage
        .from(bucketName_files)
        .move(oldPath, newPath);
      if (moveError) {
        if (
          moveError.message?.includes("not found") ||
          moveError.message?.includes("Not Found") ||
          moveError.message?.includes("404")
        ) {
          console.log("📄 [Med-A doc] No existing doc at old path, skipping move:", oldPath);
        } else {
          console.warn("Error moving Med-A doc to new path:", moveError);
        }
        return;
      }
      const { error: cacheUpdateError } = await tablesFor(supabase)
        .from("file_classification_cache")
        .update({ file_path: newPath })
        .eq("file_path", oldPath);
      if (cacheUpdateError) {
        console.warn("Error updating file_classification_cache for Med-A doc:", cacheUpdateError);
      } else {
        console.log("📁 Updated cache path:", oldPath, "->", newPath);
      }
      console.log("✅ Moved Med-A doc to new path (name/id/date change):", newPath);
    } catch (err) {
      console.warn("Error moving Med-A doc to new path:", err);
    }
  };

  const handleUpdate = async () => {
    if (readOnlyScheduleFields) return;
    let requiredFields = prompts
      .filter(([_, __, required]) => required === "Required")
      .map(([key]) => key);

    if (isInGroup(userRole, 'freemium')) {
      requiredFields = requiredFields.filter((field) => field !== "dob");
    }

    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length > 0 || !formData.date_ || !formData.time_) {
      alert(`Please fill in all required fields: ${missingFields.join(", ")}`);
      return;
    }

    const hasMultipleRowsSameEhr =
      seriesRows.length > 1 && String(patientData?.EHR_ID ?? "").trim();

    // Only tenant roles that run recurring series (student / social worker /
    // other) get the this-vs-series prompt. Clinical roles never do — a
    // returning patient's visits share an EHR ID but are not a series — and
    // the clinical-only Admin and SuperAdmin pages render this modal without a
    // userRole prop, so a missing role must default to clinical (as every
    // other role check in this modal already does) instead of prompting.
    const usesRecurringSeries =
      isInGroup(userRole, "tenantSchema") && !isInGroup(userRole, "clinical");

    if (
      !isDuplicate &&
      hasMultipleRowsSameEhr &&
      formDirtyRef.current &&
      hasPromptRelevantChanges() &&
      usesRecurringSeries
    ) {
      onSeriesScopeOpen();
      return;
    }

    seriesScopeRef.current = null;
    weekdayShiftMaskRef.current = null;
    await executeSave();
  };

  const proceedSeriesScopeOnlyThis = () => {
    seriesScopeRef.current = "single";
    weekdayShiftMaskRef.current = null;
    onSeriesScopeClose();
    executeSave();
  };

  const proceedSeriesScopeOther = () => {
    seriesScopeRef.current = "series";
    onSeriesScopeClose();
    const todayYmd = getLocalTodayYmd();
    const discoveryRows = filterSeriesRowsOnOrAfter(seriesRows, todayYmd);
    const wds = weekdaysInSeriesFromRows(discoveryRows);

    if (wds.length === 0) {
      weekdayShiftMaskRef.current = null;
      executeSave();
      return;
    }

    if (wds.length === 1) {
      weekdayShiftMaskRef.current = new Set(wds);
      executeSave();
      return;
    }

    const origDate = String(originalDateRef.current || "").trim();
    const ew = getDayOfWeekFromYmd(origDate);
    setSelectedWeekdaysForShift(
      ew != null && wds.includes(ew) ? [ew] : [wds[0]],
    );
    onWeekdayShiftOpen();
  };

  const confirmWeekdayShiftApply = () => {
    if (selectedWeekdaysForShift.length === 0) {
      alert("Select at least one weekday.");
      return;
    }
    weekdayShiftMaskRef.current = new Set(selectedWeekdaysForShift);
    onWeekdayShiftClose();
    executeSave();
  };

  const cancelWeekdayShiftDialog = () => {
    seriesScopeRef.current = null;
    weekdayShiftMaskRef.current = null;
    onWeekdayShiftClose();
  };

  const toggleWeekdayShift = (day) => {
    setSelectedWeekdaysForShift((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );
  };

  const executeSave = async () => {
    if (readOnlyScheduleFields) {
      return;
    }
    const supabase = await getSupabase();
    // Table work goes to the row's own schema; storage/auth stay on `supabase`.
    const db = tablesFor(supabase);
    const scope = seriesScopeRef.current;
    const recurringScope = scope === "series" ? "all" : "single";
    const weekdayMask = weekdayShiftMaskRef.current;
    const dateShiftDeltaDays = calendarDeltaDays(
      originalDateRef.current,
      formData.date_,
    );
    setLoading(true);
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("User not authenticated.");

      // Submission data: from form for doctors, empty for students/social workers
      const finalLinks =
        !isInGroup(userRole, 'freemium') &&
        submissionLinks.filter((link) => link && link.trim() !== "");
      const submissionData =
        !isInGroup(userRole, 'freemium')
          ? {
              [submissions_arrayUrls]: finalLinks.map((link) => ({ url: link })),
              [singleLinkSubmissionUrl]:
                finalLinks.length > 0 ? finalLinks[0] : null,
            }
          : {
              [submissions_arrayUrls]: [],
              [singleLinkSubmissionUrl]: null,
            };

      // For duplicate, only include valid fields and blank out problematic ones
      const { timezone_source: _tz, ...formDataWithoutTz } = formData;
      let patientRecord = { ...formDataWithoutTz, user_id: userId, more_audios: audioList };
      if (isDuplicate) {
        patientRecord.submission_url = "";
        patientRecord.audio = "";
        patientRecord = { ...patientRecord, ...submissionData };

        // The payload is rebuilt from scratch just below, and the real insert
        // happens further down — there used to be a throwaway insert here that
        // echoed the SOURCE row's id (formData is seeded from patientData) and
        // its client-only fields, so it always failed. Its error was never
        // checked, so the failure was silent.
        const validFields = buildUpdateFieldWhitelist();
        const excludeFields = [
          "submissions",
          "forms_url",
          "audio",
          "act_time",
          "created_at",
          "updated_at",
          "id",
          "format",
          "submission_url",
          "tel_num",
          "home_phone",
          "live_transcription_enabled",
          "displayName",
          "lastVisit",
          "visitCount",
          "timezone_source",
        ];
        patientRecord = {};
        for (const key of validFields) {
          if (key in formData && !excludeFields.includes(key)) {
            patientRecord[key] = formData[key];
          }
        }
        patientRecord.user_id = userId;
        patientRecord.submission_url = "";
        patientRecord.audio = "";
      } else {
        // For updates, filter out non-database fields
        const excludeFields = [
          "live_transcription_enabled",
          "displayName",
          "lastVisit",
          "visitCount",
          "timezone_source", // client-side only — derived from users table, not stored in notes-c
        ];
        patientRecord = {};
        for (const key in formData) {
          if (!excludeFields.includes(key)) {
            patientRecord[key] = formData[key];
          }
        }
        // Preserve original owner — don't change user_id when a shared user edits the row
        patientRecord.user_id = patientData.user_id ?? userId;

        // The picker holds the viewer's wall clock; store the owner's wall clock
        // so the table (owner→viewer conversion) shows the same time we just set.
        if (patientRecord.time_) {
          patientRecord.time_ = viewerTimeToOwner(
            patientRecord.time_,
            patientRecord.date_ || formData.date_,
            viewerTimezone,
            patientData?.timezone_source || null,
          );
        }

        // Debug: Log what's being saved
        console.log("🔍 EditPatientModal: Form data being saved:", formData);
        console.log(
          "🔍 EditPatientModal: DOB field being saved:",
          formData.dob,
        );
        console.log(
          "🔍 EditPatientModal: Patient record to save:",
          patientRecord,
        );
      }

      // PostgreSQL date/UUID columns reject empty string; normalize or drop problematic fields
      if (patientRecord.dob === "" || patientRecord.dob == null) {
        patientRecord.dob = null;
      }


      // shared_users is a UUID[] column on notes-c; the correct empty value is
      // '{}' (handled by Postgres default). If we see '[]' (string) or an empty
      // JS array, drop the key so the DB keeps or applies its own default.
      if (Object.prototype.hasOwnProperty.call(patientRecord, "shared_users")) {
        const v = patientRecord.shared_users;
        if (v === "[]" || v === "" || v == null) {
          delete patientRecord.shared_users;
        } else if (Array.isArray(v) && v.length === 0) {
          delete patientRecord.shared_users;
        }
      }

      patientRecord = stripServerOwnedFields(stripNotesTableClientOnlyFields(patientRecord));

      if (isDuplicate) {
        // Insert new patient (duplicate)
        const { data, error } = await db
          .from(tableName)
          .insert([patientRecord])
          .select();
        if (error) {
          console.error("Duplicate insert error:", error);
          console.error(explainSaveError(error, "Duplicate insert"));
          console.error("EditPatientModal: Payload that failed:", patientRecord);
          throw error;
        }

        // Schedule auto-reminder for new duplicate patient
        try {
          const hostname = window.location.hostname;
          const baseUrl =
            hostname === "localhost"
              ? BASE_URLS.local.backend
              : BASE_URLS.render.backend;
          const backendUrl = `${baseUrl}/api/reminders/schedule-auto-reminder/`;

          console.log(
            "📅 EditPatientModal: Scheduling auto-reminder for duplicate patient:",
            data[0].id,
          );

          const reminderResponse = await fetch(backendUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              patient_id: data[0].id,
              appointment_date: patientRecord.date_,
              appointment_time: patientRecord.time_,
              schema: pathSchema,
            }),
          });

          if (reminderResponse.ok) {
            const reminderData = await reminderResponse.json();
            console.log(
              "📅 EditPatientModal: Auto-reminder scheduling result:",
              reminderData,
            );
          } else {
            console.warn(
              "📅 EditPatientModal: Failed to schedule auto-reminder:",
              reminderResponse.status,
            );
          }
        } catch (reminderError) {
          console.warn(
            "📅 EditPatientModal: Error scheduling auto-reminder:",
            reminderError.message,
          );
          // Don't fail the whole operation if reminder scheduling fails
        }

        await scheduleConfirmationForPatient({
          schema: pathSchema,
          notesCId: data[0].id,
          toast,
        });

        onUpdatePatient(data[0]);
        onClose();
      } else {
        // Update existing patient — keep original owner (don't set to current user when a shared user edits)
        patientRecord.user_id = patientData.user_id ?? userId;
        // Only merge submission data for doctors (students/social workers don't have the field)
        if (!isInGroup(userRole, 'freemium')) {
          patientRecord = { ...patientRecord, ...submissionData };
        }
        if (isInGroup(userRole, 'freemium')) {
          patientRecord.notes_behavior = notesBehavior;
        }

        let data;
        if (
          recurringScope === "all" &&
          seriesRows.length > 1 &&
          originalSeriesEhrRef.current
        ) {
          const { data: fullRows, error: fullErr } = await fetchRecurringSeriesRows(
            db,
            originalSeriesEhrRef.current,
            "*",
          );
          if (fullErr) {
            console.error("Recurring series fetch error:", fullErr);
            throw fullErr;
          }
          const siblings = filterRowsByMatchingEhr(
            fullRows || [],
            originalSeriesEhrRef.current,
          );
          const anchorYmdBulk = String(originalDateRef.current || "").trim();
          const anchorBulkOk = /^\d{4}-\d{2}-\d{2}$/.test(anchorYmdBulk);
          const ymdKeyFromScheduleValue = (v) => {
            if (v instanceof Date && !Number.isNaN(v.getTime())) {
              return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
            }
            return String(v ?? "").trim().slice(0, 10);
          };
          const getWeekdayFromRow = (row) =>
            getDayOfWeekFromYmd(ymdKeyFromScheduleValue(row?.date_));
          for (const row of siblings) {
            const isCurrent = row.id === patientData.id;
            const rowYmd = ymdKeyFromScheduleValue(row.date_);
            if (
              anchorBulkOk &&
              !isCurrent &&
              rowYmd &&
              compareYmd(rowYmd, anchorYmdBulk) < 0
            ) {
              continue;
            }
            if (
              !isCurrent &&
              weekdayMask instanceof Set &&
              weekdayMask.size > 0
            ) {
              const rd = getWeekdayFromRow(row);
              if (rd != null && !weekdayMask.has(rd)) {
                continue;
              }
            }
            const shiftedSiblingDate = (() => {
              if (isCurrent) return patientRecord.date_;
              if (
                scope !== "series" ||
                !weekdayMask ||
                dateShiftDeltaDays === 0
              ) {
                return row.date_;
              }
              const origYmd = String(originalDateRef.current || "").trim();
              const rowYmdInner = ymdKeyFromScheduleValue(row.date_);
              if (origYmd && rowYmdInner && rowYmdInner <= origYmd) {
                return row.date_;
              }
              const rowDow = getWeekdayFromRow(row);
              if (rowDow == null || !weekdayMask.has(rowDow)) {
                return row.date_;
              }
              return addCalendarDaysToYmd(rowYmdInner, dateShiftDeltaDays);
            })();
            // The sibling's own confirmation state is never rewritten either.
            const payload = stripServerOwnedFields({
              ...row,
              ...patientRecord,
              date_: shiftedSiblingDate,
              time_: patientRecord.time_,
              reports: isCurrent ? patientRecord.reports : row.reports,
              audio: isCurrent ? patientRecord.audio : row.audio,
              transcripts: isCurrent ? patientRecord.transcripts : row.transcripts,
              more_audios: isCurrent ? patientRecord.more_audios : row.more_audios,
              user_id: row.user_id ?? patientRecord.user_id,
            });
            delete payload.id;
            delete payload.created_at;
            const { error: upErr } = await db
              .from(tableName)
              .update(payload)
              .eq("id", row.id);
            if (upErr) {
              console.error("Update error (recurring series):", upErr);
              console.error(explainSaveError(upErr, "Update"));
              throw upErr;
            }
          }
          const { data: currentRow, error: curErr } = await db
            .from(tableName)
            .select("*")
            .eq("id", patientData.id)
            .maybeSingle();
          if (curErr) throw curErr;
          data = currentRow ? [currentRow] : [];
        } else {
          const { data: updateData, error } = await db
            .from(tableName)
            .update(patientRecord)
            .eq("id", patientData.id)
            .select();
          if (error) {
            console.error("Update error:", error);
            console.error(explainSaveError(error, "Update"));
            console.error("EditPatientModal: Payload that failed:", patientRecord);
            throw error;
          }
          data = updateData;
        }

        // PostgREST answers a zero-row UPDATE with `200 []` and no error, so
        // silence here means "nothing was saved" — the row isn't visible in the
        // schema we aimed at, or RLS there refused the write. Stop before the
        // file moves / reminder / onUpdatePatient, all of which assume data[0]
        // exists and otherwise fail with an unrelated "reading 'id'".
        if (!data?.[0]) {
          throw new Error(
            `No row was updated: '${tableName}' id=${patientData.id} in schema ` +
              `'${patientSchema || supabaseSchema || "public"}'. The row may have been ` +
              `deleted, or that schema's row-level security blocked the update.`,
          );
        }

        // Optional: create additional recurring weekly entries (freemium only); not when updating entire series
        if (
          recurringScope === "single" &&
          isRecurring &&
          isInGroup(userRole, 'freemium') &&
          recurringWeeks >= 1 &&
          data?.[0]
        ) {
          const inExistingSeries = seriesRows.length > 1;

          const baseRecord = {
            ...patientRecord,
            notes_behavior: notesBehavior,
          };
          delete baseRecord.id;
          delete baseRecord.created_at;

          /** Canonical YYYY-MM-DD for Set lookups (handles ISO strings, loose DB formatting). */
          const normalizeCalendarYmd = (v) => {
            const raw = String(v ?? "").trim();
            if (!raw) return "";
            const head = raw.slice(0, 10);
            const m = head.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
            if (!m) return "";
            const y = Number(m[1]);
            const mo = Number(m[2]);
            const day = Number(m[3]);
            if (!y || !mo || !day) return "";
            return `${y}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          };

          const toYmd = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

          /**
           * DB/API may return date-only as string, ISO string, or Date — avoid missing rows in existingDates.
           */
          const coerceScheduleDateToYmdKey = (v) => {
            if (v == null || v === "") return "";
            if (v instanceof Date && !Number.isNaN(v.getTime())) {
              return toYmd(v);
            }
            const raw = String(v).trim();
            if (!raw) return "";
            if (/^\d{4}-\d{1,2}-\d{1,2}/.test(raw.slice(0, 10))) {
              return normalizeCalendarYmd(raw);
            }
            const t = Date.parse(raw);
            if (!Number.isNaN(t)) {
              return toYmd(new Date(t));
            }
            return normalizeCalendarYmd(raw);
          };

          const parseLocalYmd = (ymd) => {
            const n = normalizeCalendarYmd(ymd);
            if (!n) return null;
            const [y, m, d] = n.split("-").map(Number);
            return y && m && d ? new Date(y, m - 1, d) : null;
          };

          const seriesEhrId = String(formData.EHR_ID ?? patientData.EHR_ID ?? "").trim();
          const { data: seriesDateRows } = await fetchRecurringSeriesRows(
            supabase,
            seriesEhrId,
            "date_, EHR_ID",
          );
          const existingDates = new Set();
          for (const r of seriesDateRows || []) {
            const n = coerceScheduleDateToYmdKey(r?.date_);
            if (n) existingDates.add(n);
          }
          for (const r of seriesRows || []) {
            const n = coerceScheduleDateToYmdKey(r?.date_);
            if (n) existingDates.add(n);
          }
          for (const seed of [
            patientRecord.date_,
            patientData?.date_,
            formData?.date_,
          ]) {
            const n = coerceScheduleDateToYmdKey(seed);
            if (n) existingDates.add(n);
          }

          const entriesToInsert = [];

          if (inExistingSeries) {
            const fallbackBaseDate = parseLocalYmd(patientRecord.date_);
            const allParsedDates = [...existingDates]
              .map(parseLocalYmd)
              .filter(Boolean)
              .sort((a, b) => a.getTime() - b.getTime());
            const latestSeriesDate =
              allParsedDates.length > 0
                ? allParsedDates[allParsedDates.length - 1]
                : fallbackBaseDate;
            if (!latestSeriesDate) {
              throw new Error("Missing valid appointment date for recurring expansion.");
            }
            const latestDow = latestSeriesDate.getDay();
            const activeDays =
              recurringDays.length > 0 ? recurringDays : [latestDow];

            // Anchor after the last date that already uses one of the selected weekdays — not after the
            // latest date in the series overall. Otherwise Mon in week 4 can push "Wed only" adds to week 5
            // while week 3 Wed was still missing.
            const datesMatchingActiveWeekdays = allParsedDates.filter((d) =>
              activeDays.includes(d.getDay()),
            );
            const anchorForCursor =
              datesMatchingActiveWeekdays.length > 0
                ? datesMatchingActiveWeekdays[datesMatchingActiveWeekdays.length - 1]
                : latestSeriesDate;

            const cursor = new Date(anchorForCursor);
            cursor.setDate(cursor.getDate() + 1);
            // One "week" of add-more = one new row per selected weekday (next free slot each).
            const targetInsertCount = Math.max(
              1,
              recurringWeeks * Math.max(1, activeDays.length),
            );
            let guard = 0;
            while (entriesToInsert.length < targetInsertCount && guard < 800) {
              guard += 1;
              if (activeDays.includes(cursor.getDay())) {
                const ymd = coerceScheduleDateToYmdKey(toYmd(cursor));
                if (ymd && !existingDates.has(ymd)) {
                  existingDates.add(ymd);
                  entriesToInsert.push({ ...baseRecord, date_: ymd });
                }
              }
              cursor.setDate(cursor.getDate() + 1);
            }
          } else {
            const [by, bm, bd] = (patientRecord.date_ || "").split("-").map(Number);
            const baseDate = new Date(by, bm - 1, bd);
            if (Number.isNaN(baseDate.getTime())) {
              throw new Error("Missing valid appointment date for recurring pattern.");
            }
            const baseDayOfWeek = baseDate.getDay();
            const activeDays =
              recurringDays.length > 0 ? recurringDays : [baseDayOfWeek];

            activeDays.forEach((targetDay) => {
              let diff = targetDay - baseDayOfWeek;
              if (diff < 0) diff += 7;
              const firstOcc = new Date(baseDate);
              firstOcc.setDate(baseDate.getDate() + diff);
              const startWeek = diff === 0 ? 1 : 0;
              for (let week = startWeek; week < recurringWeeks; week++) {
                const nextDate = new Date(firstOcc);
                nextDate.setDate(firstOcc.getDate() + week * 7);
                const nextDateString = normalizeCalendarYmd(toYmd(nextDate));
                const curOnly = normalizeCalendarYmd(patientRecord.date_);
                if (
                  nextDateString &&
                  !existingDates.has(nextDateString) &&
                  nextDateString !== curOnly
                ) {
                  existingDates.add(nextDateString);
                  entriesToInsert.push({ ...baseRecord, date_: nextDateString });
                }
              }
            });
            entriesToInsert.sort((a, b) => a.date_.localeCompare(b.date_));
          }

          if (entriesToInsert.length > 0) {
            const { error: insertError } = await db
              .from(tableName)
              .insert(entriesToInsert)
              .select();
            if (insertError) {
              console.warn("EditPatientModal: recurring insert failed", insertError);
            } else if (onUpdatePatient) {
              localStorage.setItem("patientUpdated", patientData.id);
            }
          }
        }

        // Set localStorage flag to notify PatientPage to refresh
        localStorage.setItem("patientUpdated", patientData.id);

        // When name, id, or date change, move this meeting's Med-A doc to the new path
        // so the same doc stays with the meeting instead of being left at the old path.
        await moveMedADocToNewPath(supabase, patientData, formData);

        // If the date changed, move reports and Med-A docs to the new date folder
        if (patientData.date_ && formData.date_ && patientData.date_ !== formData.date_) {
          console.log(`📅 Date changed from ${patientData.date_} to ${formData.date_}, moving reports...`);
          const reportsPath = patientData.reports;
          const formatDateForPath = (dateStr) => {
            const [y, m, d] = (dateStr || "").split("-");
            return y && m && d ? `${m}-${d}-${y.slice(2)}` : "";
          };
          const entityName = formData[nameFieldKey] || patientData[nameFieldKey];
          const ehrId = formData.EHR_ID ?? patientData.EHR_ID ?? "";
          const baseReportsPath = `New/Reports/${(entityName || "Unknown").replace(/\//g, "-")}_${ehrId}`.replace(/_$/, "_");
          let newFolderPath = await moveReportsToNewDate(
            supabase,
            patientData.date_,
            formData.date_,
            reportsPath,
          );
          if (!newFolderPath && reportsPath) newFolderPath = null;
          if (!newFolderPath && !reportsPath) {
            newFolderPath = `${baseReportsPath}/${formatDateForPath(formData.date_)}`;
          }
          if (newFolderPath) {
            const { error: reportsUpdateError } = await db
              .from(tableName)
              .update({ reports: newFolderPath })
              .eq("id", patientData.id);

            if (reportsUpdateError) {
              console.error("Error updating reports path:", reportsUpdateError);
            } else {
              console.log(`✅ Updated reports path to: ${newFolderPath}`);
            }
          }

          // Move Med-A docs to the new date folder (so doc page and reports stay in sync)
          await moveMedADocsToNewDate(
            supabase,
            patientData.date_,
            formData.date_,
            ehrId,
            entityName,
          );

          console.log(`📅 [Edit entity] Filepath fix complete. See [Reports filepath fix] and [Med-A docs filepath fix] logs above for success/failure.`);

          // Re-categorize ALL SMS appointment_dates for this patient — on
          // every number they have (cell and home: texts can go to either,
          // see utils/smsPhone), one pass per number.
          const patientPhones = rowPhones({
            tel_num: formData.tel_num || patientData.tel_num,
            home_phone: formData.home_phone || patientData.home_phone,
          });
          for (const patientPhone of patientPhones) {
            const normalizedPhone = patientPhone.replace(/\D/g, "").slice(-10);
            const phonePattern = `%${normalizedPhone}`;
            console.log(`📱 Re-categorizing ALL SMS for phone ${normalizedPhone} (date changed from ${patientData.date_} to ${formData.date_})`);

            try {
              // 1. Fetch ALL SMS rows for this patient's phone number
              const { data: smsRows, error: smsFetchError } = await db
                .from("SMS")
                .select("*")
                .or(`from.ilike.${phonePattern},to.ilike.${phonePattern}`);

              if (smsFetchError) {
                console.error("Error fetching SMS rows:", smsFetchError);
              } else if (smsRows && smsRows.length > 0) {
                console.log(`📱 Found ${smsRows.length} total SMS rows to re-categorize`);

                // 2. Call backend to re-categorize using match_message_to_appointment logic
                const hostname = window.location.hostname;
                const backendBase =
                  hostname === "localhost"
                    ? BASE_URLS.local.backend
                    : BASE_URLS.render.backend;

                const catResponse = await fetch(
                  `${backendBase}/twilio/categorize-messages/`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      messages: smsRows,
                      patient_phone: patientPhone,
                      schema: pathSchema,
                    }),
                  },
                );

                if (catResponse.ok) {
                  const catResult = await catResponse.json();
                  const { categorized, uncategorized } = catResult;

                  // 3. Update each SMS row's appointment_date based on its new category
                  for (const [apptDate, section] of Object.entries(categorized || {})) {
                    const messageIds = (section.messages || [])
                      .map((m) => m.id)
                      .filter(Boolean);
                    if (messageIds.length > 0) {
                      const { error: updateErr } = await db
                        .from("SMS")
                        .update({ appointment_date: apptDate })
                        .in("id", messageIds);
                      if (updateErr) {
                        console.error(`Error updating SMS to ${apptDate}:`, updateErr);
                      } else {
                        console.log(`✅ Updated ${messageIds.length} SMS rows to appointment_date: ${apptDate}`);
                      }
                    }
                  }

                  // Clear appointment_date for uncategorized messages
                  if (uncategorized && uncategorized.length > 0) {
                    const uncatIds = uncategorized.map((m) => m.id).filter(Boolean);
                    if (uncatIds.length > 0) {
                      const { error: uncatErr } = await db
                        .from("SMS")
                        .update({ appointment_date: null })
                        .in("id", uncatIds);
                      if (uncatErr) {
                        console.error("Error clearing uncategorized SMS appointment_date:", uncatErr);
                      } else {
                        console.log(`✅ Cleared appointment_date for ${uncatIds.length} uncategorized SMS rows`);
                      }
                    }
                  }
                } else {
                  console.error("Failed to categorize SMS messages:", catResponse.status);
                }
              } else {
                console.log("📱 No SMS rows found for this patient");
              }
            } catch (smsError) {
              console.error("Error re-categorizing SMS:", smsError);
              // Don't fail the whole operation if SMS re-categorization fails
            }
          }
        }

        // Schedule auto-reminder for updated patient
        try {
          const hostname = window.location.hostname;
          const baseUrl =
            hostname === "localhost"
              ? BASE_URLS.local.backend
              : BASE_URLS.render.backend;
          const backendUrl = `${baseUrl}/api/reminders/schedule-auto-reminder/`;

          console.log(
            "📅 EditPatientModal: Scheduling auto-reminder for updated patient:",
            data[0].id,
          );

          const reminderResponse = await fetch(backendUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              patient_id: data[0].id,
              appointment_date: patientRecord.date_,
              appointment_time: patientRecord.time_,
              schema: pathSchema,
            }),
          });

          if (reminderResponse.ok) {
            const reminderData = await reminderResponse.json();
            console.log(
              "📅 EditPatientModal: Auto-reminder scheduling result:",
              reminderData,
            );
          } else {
            console.warn(
              "📅 EditPatientModal: Failed to schedule auto-reminder:",
              reminderResponse.status,
            );
          }
        } catch (reminderError) {
          console.warn(
            "📅 EditPatientModal: Error scheduling auto-reminder:",
            reminderError.message,
          );
          // Don't fail the whole operation if reminder scheduling fails
        }

        await scheduleConfirmationForPatient({
          schema: pathSchema,
          notesCId: data[0].id,
          toast,
        });

        onUpdatePatient(data[0]);
        onClose();
      }
    } catch (error) {
      console.error("Error updating patient:", error.message);
      const explanation = explainSaveError(error, "Save");
      if (explanation) console.error(explanation);
      alert(
        `Failed to update patient: ${error?.message || "unknown error"}\n\n` +
          "See the console for details.",
      );
    } finally {
      seriesScopeRef.current = null;
      weekdayShiftMaskRef.current = null;
      setLoading(false);
    }
    window.location.reload();
  };

  /** Multiple schedule rows share this EHR ID (any role — not tied to freemium recurring feature). */
  const isRecurringSeries =
    seriesRows.length > 1 && String(formData?.EHR_ID ?? patientData?.EHR_ID ?? "").trim();
  const latestSeriesDateForPreview = (() => {
    if (!isRecurringSeries) return "";
    const validDates = (seriesRows || [])
      .map((row) => String(row?.date_ || "").trim())
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
      .sort();
    return validDates.length > 0 ? validDates[validDates.length - 1] : "";
  })();

  const dayNameLabels = ["Su", "M", "Tu", "W", "Th", "F", "Sa"];
  const previewActiveDaysForSeries = (() => {
    if (!isRecurringSeries) return [];
    if (recurringDays.length > 0) return [...recurringDays].sort((a, b) => a - b);
    const fromToday = weekdaysInSeriesFromRows(
      filterSeriesRowsOnOrAfter(seriesRows, getLocalTodayYmd()),
    );
    if (fromToday.length > 0) return fromToday;
    if (!latestSeriesDateForPreview) return [];
    const [py, pm, pd] = latestSeriesDateForPreview.split("-").map(Number);
    return [new Date(py, pm - 1, pd).getDay()];
  })();
  const previewPatternSeries = previewActiveDaysForSeries
    .map((d) => dayNameLabels[d])
    .join("/");

  const dateShiftDeltaPreview = calendarDeltaDays(
    originalDateRef.current,
    formData.date_,
  );
  const dateShiftSummaryText =
    dateShiftDeltaPreview === 0
      ? "no calendar change"
      : dateShiftDeltaPreview > 0
        ? `${dateShiftDeltaPreview} calendar day${dateShiftDeltaPreview === 1 ? "" : "s"} later`
        : `${Math.abs(dateShiftDeltaPreview)} calendar day${Math.abs(dateShiftDeltaPreview) === 1 ? "" : "s"} earlier`;

  const seriesWeekdayPickerHasDateShift = dateShiftDeltaPreview !== 0;

  const seriesRowsForWeekdayPrompt = filterSeriesRowsOnOrAfter(
    seriesRows,
    getLocalTodayYmd(),
  );
  const weekdaysInSeriesList = weekdaysInSeriesFromRows(seriesRowsForWeekdayPrompt);
  const seriesWeekdayCounts = (() => {
    const m = {};
    (seriesRowsForWeekdayPrompt || []).forEach((r) => {
      const w = getDayOfWeekFromYmd(r?.date_);
      if (w != null) m[w] = (m[w] || 0) + 1;
    });
    return m;
  })();

  /** After paint when the weekday dialog becomes visible — captures state as rendered (incl. selected weekdays). */
  useLayoutEffect(() => {
    const wasOpen = prevWeekdayShiftOpenRef.current;
    prevWeekdayShiftOpenRef.current = isWeekdayShiftOpen;
    if (!isWeekdayShiftOpen || wasOpen) return;

    const editedAnchor = String(originalDateRef.current || "").trim();
    const todayYmd = getLocalTodayYmd();
    const ehr =
      patientData?.EHR_ID ?? formData?.EHR_ID ?? originalSeriesEhrRef.current;
    const future = filterSeriesRowsOnOrAfter(seriesRows, todayYmd);
    const wds = weekdaysInSeriesFromRows(future);
    const labels = ["Su", "M", "Tu", "W", "Th", "F", "Sa"];
    const dowLabel = (ymd) => {
      const d = getDayOfWeekFromYmd(ymd);
      return d != null ? labels[d] : null;
    };
    const counts = {};
    (future || []).forEach((r) => {
      const d = getDayOfWeekFromYmd(r?.date_);
      if (d != null) counts[d] = (counts[d] || 0) + 1;
    });

    console.log(
      "[EditPatientModal] Which weekdays should move? — modal open (after paint)",
      {
        patientId: patientData?.id,
        ehrPatient: patientData?.EHR_ID,
        ehrForm: formData?.EHR_ID,
        ehrNormalized: normalizeEhrIdForMatch(ehr),
        originalSeriesEhrRef: originalSeriesEhrRef.current,
        editedOccurrenceDate: editedAnchor,
        discoveryAnchorDate: todayYmd,
        formDate_: formData?.date_,
        dateShiftDeltaPreview: calendarDeltaDays(
          originalDateRef.current,
          formData?.date_,
        ),
        seriesRowsCount: seriesRows.length,
        seriesRowsSnapshot: (seriesRows || []).map((r) => ({
          id: r.id,
          date_: r.date_,
          EHR_ID: r.EHR_ID,
          dow: getDayOfWeekFromYmd(r?.date_),
          dowLabel: dowLabel(r?.date_),
        })),
        discoveryRowCount: future.length,
        discoveryRowsSnapshot: future.map((r) => ({
          id: r.id,
          date_: r.date_,
          EHR_ID: r.EHR_ID,
          dow: getDayOfWeekFromYmd(r?.date_),
          dowLabel: dowLabel(r?.date_),
        })),
        weekdaysInSeriesList: wds,
        weekdayLabelList: wds.map((i) => labels[i]),
        seriesWeekdayCounts: counts,
        selectedWeekdaysForShift,
      },
    );
  }, [
    isWeekdayShiftOpen,
    seriesRows,
    formData?.date_,
    formData?.EHR_ID,
    patientData?.id,
    patientData?.EHR_ID,
    selectedWeekdaysForShift,
  ]);

  const rowOwnerForDisplay = hasPreloadedRowOwner ? preloadedRowOwner : rowOwner;
  const rowOwnerLabel = (() => {
    const name = rowOwnerForDisplay?.name && String(rowOwnerForDisplay.name).trim();
    const email = rowOwnerForDisplay?.email && String(rowOwnerForDisplay.email).trim();
    if (name) return name;
    if (email) return email;
    if (rowOwnerLoading) return "";
    const ownerId = patientData?.user_id;
    return ownerId ? `User (${String(ownerId).slice(0, 8)}...)` : "";
  })();

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isDuplicate ? `Duplicate ${getLabel(userRole, 'entity')}` : `Edit ${getLabel(userRole, 'entity')}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {readOnlyScheduleFields && (
            <Alert status="info" mb={4} borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">View-only schedule</AlertTitle>
                <AlertDescription fontSize="sm">
                  You can open the document, but you cannot edit this schedule row
                  or delete it with your current shared permission (viewer or
                  editor).
                </AlertDescription>
              </Box>
            </Alert>
          )}
          {rowOwnerLabel && (
            <FormControl mt={2} mb={4}>
              <FormLabel>Row owner</FormLabel>
              <Input value={rowOwnerLabel} isReadOnly bg="gray.50" />
              {rowOwnerForDisplay?.email &&
                rowOwnerForDisplay?.name &&
                String(rowOwnerForDisplay.email).trim() !== String(rowOwnerForDisplay.name).trim() && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    {rowOwnerForDisplay.email}
                  </Text>
                )}
            </FormControl>
          )}
          {/* Patient/Class/Meeting Suggestion Section - hide for freemium */}
          {!isInGroup(userRole, 'freemium') && (
            <VStack spacing={4} align="stretch" mb={6}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Quick Edit: Search for other {getLabel(userRole, 'entity').toLowerCase()}s to copy their information
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">Search Other {getLabel(userRole, 'entity')}s</FormLabel>
                <PatientSuggestionDropdown
                  value={suggestedPatientName}
                  onChange={handlePatientNameSuggestion}
                  onPatientSelect={handlePatientSelect}
                  placeholder={`Type ${getLabel(userRole, 'entity').toLowerCase()} name to search...`}
                />
              </FormControl>
              <Divider />
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Or manually edit {getLabel(userRole, 'entity').toLowerCase()} information:
              </Text>
            </VStack>
          )}

          {/* Name Fields - Different for students vs others */}
          {normalizeRole(userRole) === 'student' ? (
            <FormControl isRequired mt={4}>
              <FormLabel>{getLabel(userRole, 'nameField')}</FormLabel>
              <Input
                value={formData[nameFieldKey] || ""}
                onChange={(e) => handleChange(nameFieldKey, e.target.value)}
                placeholder="Enter class name"
              />
            </FormControl>
          ) : (
            <>
              <HStack spacing={4} mt={4}>
                <FormControl isRequired>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={nameComponents.firstName}
                    onChange={(e) => handleNameChange("firstName", e.target.value)}
                    placeholder="First Name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Middle Name</FormLabel>
                  <Input
                    value={nameComponents.middleName}
                    onChange={(e) => handleNameChange("middleName", e.target.value)}
                    placeholder="Middle Name"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={nameComponents.lastName}
                    onChange={(e) => handleNameChange("lastName", e.target.value)}
                    placeholder="Last Name"
                  />
                </FormControl>
              </HStack>

              <FormControl mt={4}>
                <FormLabel>Name (as will appear)</FormLabel>
                <Input
                  value={formData[nameFieldKey] || ""}
                  isReadOnly
                  bg="gray.50"
                  placeholder="Last, First Middle"
                />
              </FormControl>
            </>
          )}

          {prompts
            .filter(([key]) => {
              // Filter out name field (handled above)
              if (key === nameFieldKey) return false;

              // Only show fields that exist in this role's schedules table (exclude hidden columns)
              const hiddenColumns = getScheduleField(userRole, 'hiddenColumns') || [];
              if (hiddenColumns.includes(key)) return false;

              return true;
            })
            .map(([key, label, required]) => {
              // Adjust labels for role-specific fields (match Add modal)
              let displayLabel = label;
              if (key === "EHR_ID") {
                displayLabel = getLabel(userRole, 'idField');
              } else if (key === "physician_name") {
                displayLabel = getLabel(userRole, 'physicianField');
              } else if (key === "email") {
                // Email is optional for freemium users
                required = isInGroup(userRole, 'freemium') ? "Optional" : required;
              }

              return (
                <FormControl
                  key={key}
                  isRequired={required === "Required"}
                  mt={4}
                >
                  <FormLabel>{displayLabel}</FormLabel>
                  {key === "dob" ? (
                    <Input
                      type="date"
                      value={formData[key] || ""}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter ${displayLabel}`}
                    />
                  ) : (
                    <Input
                      value={formData[key] || ""}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter ${displayLabel}`}
                    />
                  )}
                </FormControl>
              );
            })}

          {/* Optional per-row template override. Takes priority over Type when
              set. Deliberately not part of `prompts`, which renders plain text
              inputs and drives required-field validation — it is whitelisted
              for the update payload via buildUpdateFieldWhitelist(). */}
          <FormControl mt={4}>
            <FormLabel>Template</FormLabel>
            <Select
              value={formData.template || ""}
              onChange={(e) => handleChange("template", e.target.value)}
              placeholder="Use type"
              isDisabled={readOnlyScheduleFields}
            >
              {withStaleOption(templateOptions, formData.template).map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.600" mt={1}>
              Optional. Overrides the Type field when choosing this document's template.
            </Text>
          </FormControl>

          {/* Time Input Field with scroll selector - same order as Add modal */}
          <FormControl isRequired mt={4}>
            <FormLabel>Time</FormLabel>
            <HStack>
              <Select
                value={String(formData.time_ || "00:00").slice(0, 2)}
                onChange={(e) =>
                  handleTimeInputChange(
                    `${e.target.value}:${String(formData.time_ || "00:00").slice(3, 5) || "00"}`,
                  )
                }
                width="80px"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={String(i).padStart(2, "0")}>
                    {String(i).padStart(2, "0")}
                  </option>
                ))}
              </Select>

              <Select
                value={String(formData.time_ || "00:00").slice(3, 5) || "00"}
                onChange={(e) =>
                  handleTimeInputChange(
                    `${String(formData.time_ || "00:00").slice(0, 2) || "00"}:${e.target.value}`,
                  )
                }
                width="80px"
              >
                <option value="00">00</option>
                <option value="15">15</option>
                <option value="30">30</option>
                <option value="45">45</option>
              </Select>
            </HStack>
          </FormControl>

          {/* Date Input Field - same label as Add modal */}
          <FormControl isRequired mt={4}>
            <FormLabel>Select Date</FormLabel>
            <Input
              type="date"
              value={formData.date_ || ""}
              onChange={(e) => handleChange("date_", e.target.value)}
            />
          </FormControl>

          {/* Submission Links - only if this role's table shows the submissions column */}
          {!(getScheduleField(userRole, 'hiddenColumns') || []).includes("submissions_array") && (
            <FormControl mt={4}>
              <FormLabel>Submission Links</FormLabel>
              {submissionLinks.map((link, index) => (
                <Flex key={index} mb={2}>
                  <Input
                    value={link}
                    onChange={(e) => handleLinkChange(index, e.target.value)}
                    placeholder="https://example.com/submission"
                    mr={2}
                  />
                  <IconButton
                    aria-label="Delete link"
                    icon={<DeleteIcon />}
                    colorScheme="red"
                    onClick={() => handleDeleteLink(index)}
                  />
                </Flex>
              ))}
              <Button onClick={handleAddLink} size="sm" mt={2}>
                Add Link
              </Button>
            </FormControl>
          )}

          {/* Audio files */}
          <FormControl mt={4}>
            <FormLabel>Audios</FormLabel>
            {audioList.length === 0 && (
              <Text fontSize="sm" color="gray.500" mb={2}>No audio files yet.</Text>
            )}
            {audioList.map((audio, index) => (
              <Flex key={index} mb={2} align="center" gap={2}>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                    {recordingLabel(audio, index + 1)}
                  </Text>
                  {audio.url && (
                    <audio controls src={audio.playbackUrl || audio.url} style={{ width: '100%', marginTop: '4px', height: '32px' }} />
                  )}
                </Box>
                {audio.url && (
                  <IconButton
                    as="a"
                    href={audio.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open audio"
                    icon={<ExternalLinkIcon />}
                    size="sm"
                    variant="ghost"
                  />
                )}
                <IconButton
                  aria-label="Delete audio"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  size="sm"
                  onClick={() => handleDeleteAudio(index)}
                />
              </Flex>
            ))}
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files?.[0]) handleUploadAudio(e.target.files[0]); e.target.value = ''; }}
            />
            <Button
              size="sm"
              mt={2}
              isLoading={audioUploading}
              loadingText="Uploading…"
              onClick={() => audioInputRef.current?.click()}
            >
              Add Audio
            </Button>
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Transcripts</FormLabel>
            {transcriptList.length === 0 && (
              <Text fontSize="sm" color="gray.500" mb={2}>No transcripts uploaded.</Text>
            )}
            {transcriptList.map((t, index) => (
              <Flex key={t.id || index} align="center" mb={1} gap={2}>
                <Text fontSize="sm" flex={1} noOfLines={1}>
                  {t.audio_file_name || t.name || `Transcript ${index + 1}`}
                </Text>
                <IconButton
                  aria-label="Delete transcript"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  size="sm"
                  onClick={async () => {
                    const updated = transcriptList.filter((_, i) => i !== index);
                    setTranscriptList(updated);
                    try {
                      const supabase = await getSupabase();
                      await tablesFor(supabase).from(tableName).update({ transcripts: updated }).eq('id', patientData.id);
                      if (onUpdatePatient) onUpdatePatient({ ...patientData, transcripts: updated });
                    } catch (e) {
                      console.error('Failed to delete transcript:', e);
                    }
                  }}
                />
              </Flex>
            ))}
            <input
              ref={transcriptInputRef}
              type="file"
              accept=".txt,.json,.md"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files?.[0]) handleUploadTranscript(e.target.files[0]); e.target.value = ''; }}
            />
            <Button
              size="sm"
              mt={2}
              isLoading={transcriptUploading}
              loadingText="Uploading…"
              onClick={() => transcriptInputRef.current?.click()}
            >
              Add Transcript
            </Button>
          </FormControl>

          {/* Recurring schedule option (freemium only) — aligned with AddPatientModal */}
          {isInGroup(userRole, 'freemium') && (
            <VStack align="stretch" mt={4} spacing={3} p={4} bg="blue.50" borderRadius="md">
              {isRecurringSeries && (
                <Text fontSize="sm" color="gray.700">
                  This row is already part of a recurring series. You can still add more occurrences
                  below, or use Save to update only this date or all dates in the series (when you have unsaved changes).
                </Text>
              )}
              <Checkbox
                isChecked={isRecurring}
                onChange={(e) => {
                  const checked = e.target.checked;
                  formDirtyRef.current = true;
                  recurringOptionsDirtyRef.current = true;
                  setIsRecurring(checked);
                  if (checked && formData.date_) {
                    if (isRecurringSeries && seriesRows.length > 1) {
                      const discovery = filterSeriesRowsOnOrAfter(
                        seriesRows,
                        getLocalTodayYmd(),
                      );
                      const wds = weekdaysInSeriesFromRows(discovery);
                      if (wds.length > 0) {
                        setRecurringDays(wds);
                      } else {
                        const [y, m, d] = formData.date_.split("-").map(Number);
                        setRecurringDays([new Date(y, m - 1, d).getDay()]);
                      }
                    } else {
                      const [y, m, d] = formData.date_.split("-").map(Number);
                      setRecurringDays([new Date(y, m - 1, d).getDay()]);
                    }
                    if (!isRecurringSeries) {
                      setRecurringWeeks(8);
                    } else {
                      setRecurringWeeks(1);
                    }
                  } else {
                    setRecurringDays([]);
                    setRecurringWeeks(1);
                  }
                }}
                colorScheme="blue"
              >
                <Text fontWeight="medium">
                  {isRecurringSeries
                    ? "Add more meetings"
                    : `Make this a recurring ${getLabel(userRole, 'entity').toLowerCase()}`}
                </Text>
              </Checkbox>
              {isRecurring && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Days of week</FormLabel>
                    <HStack spacing={3} flexWrap="wrap">
                      {[
                        { label: "Su", value: 0 },
                        { label: "M", value: 1 },
                        { label: "Tu", value: 2 },
                        { label: "W", value: 3 },
                        { label: "Th", value: 4 },
                        { label: "F", value: 5 },
                        { label: "Sa", value: 6 },
                      ].map(({ label, value }) => (
                        <Checkbox
                          key={value}
                          isChecked={recurringDays.includes(value)}
                          onChange={() => toggleRecurringDay(value)}
                          colorScheme="blue"
                          size="sm"
                        >
                          {label}
                        </Checkbox>
                      ))}
                    </HStack>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      {isRecurringSeries
                        ? "How many more meetings to add"
                        : "Number of weeks"}
                    </FormLabel>
                    <NumberInput
                      value={recurringWeeks}
                      onChange={(valueString) => {
                        formDirtyRef.current = true;
                        recurringOptionsDirtyRef.current = true;
                        setRecurringWeeks(parseInt(valueString, 10) || 1);
                      }}
                      min={1}
                      max={52}
                      size="sm"
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    {isRecurringSeries ? (
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        {(() => {
                          const addMoreTotal =
                            recurringDays.length > 0
                              ? recurringWeeks * recurringDays.length
                              : recurringWeeks;
                          return (
                            <>
                              Adds {addMoreTotal} new meeting{addMoreTotal === 1 ? "" : "s"} (next open slot
                              per selected day) after{" "}
                              {latestSeriesDateForPreview || "the latest date in this series"}, on{" "}
                              <strong>{previewPatternSeries || "—"}</strong>. Skips any date that already has a
                              meeting for this {getLabel(userRole, 'idField')}.
                            </>
                          );
                        })()}
                      </Text>
                    ) : (
                      (() => {
                        const getBaseDayOfWeek = () => {
                          if (!formData.date_) return -1;
                          const [y, m, d] = formData.date_.split("-").map(Number);
                          return new Date(y, m - 1, d).getDay();
                        };
                        const baseDayOfWeek = getBaseDayOfWeek();
                        const activeDays =
                          recurringDays.length > 0
                            ? recurringDays
                            : baseDayOfWeek >= 0
                              ? [baseDayOfWeek]
                              : [];
                        const baseDayIncluded = activeDays.includes(baseDayOfWeek);
                        const totalEntries =
                          baseDayIncluded
                            ? activeDays.length * recurringWeeks
                            : 1 + activeDays.length * recurringWeeks;
                        const pattern = [...activeDays]
                          .sort((a, b) => a - b)
                          .map((d) => dayNameLabels[d])
                          .join("/");
                        return (
                          <Text fontSize="xs" color="gray.600" mt={1}>
                            Pattern: <strong>{pattern || "—"}</strong> — about {totalEntries} total entries
                            over {recurringWeeks} week{recurringWeeks !== 1 ? "s" : ""}, starting{" "}
                            {formData.date_ || "the selected date"} (this date is updated in place; new rows
                            are added for other dates only).
                          </Text>
                        );
                      })()
                    )}
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Notes behavior each session</FormLabel>
                    <RadioGroup
                      value={notesBehavior}
                      onChange={(v) => {
                        markFieldDirty("notes_behavior");
                        setNotesBehavior(v);
                      }}
                    >
                      <Stack direction="row" spacing={4}>
                        <Radio value="accumulate" colorScheme="blue" size="sm">
                          Carry over notes
                        </Radio>
                        <Radio value="fresh" colorScheme="blue" size="sm">
                          Start fresh
                        </Radio>
                      </Stack>
                    </RadioGroup>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {notesBehavior === "accumulate"
                        ? "When opening an empty session, you'll be offered the option to copy notes from the previous session."
                        : "Each session will open with a blank template."}
                    </Text>
                  </FormControl>
                </>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleUpdate}
            isLoading={loading}
            isDisabled={readOnlyScheduleFields}
          >
            Save Changes
          </Button>
          <CollaborationDebugToggle />
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <AlertDialog
      isOpen={isSeriesScopeOpen}
      leastDestructiveRef={cancelSeriesScopeRef}
      onClose={onSeriesScopeClose}
    >
      <AlertDialogOverlay />
      <AlertDialogContent maxW={{ base: "calc(100vw - 2rem)", md: "xl" }} mx={4}>
        <AlertDialogHeader fontSize="lg" fontWeight="bold">
          This meeting only, or other meetings in the series too?
        </AlertDialogHeader>
        <AlertDialogBody>
          This {getLabel(userRole, 'entity').toLowerCase()} is part of a recurring series ({seriesRows.length}{" "}
          dates, same EHR ID). Choose whether your edits apply to{" "}
          <strong>only this occurrence</strong> or to <strong>this date and later</strong> in the series
          (past dates are left unchanged). Shared fields like time, type, and notes behavior apply to each
          included row. Per-date files (audio, reports) stay on each date unless you change only this row.
        </AlertDialogBody>
        <AlertDialogFooter
          flexDirection="column"
          alignItems="stretch"
          gap={3}
          flexWrap="nowrap"
        >
          <Button
            colorScheme="blue"
            onClick={proceedSeriesScopeOther}
            whiteSpace="normal"
            h="auto"
            py={3}
          >
            Other meetings in this series too
          </Button>
          <Button
            onClick={proceedSeriesScopeOnlyThis}
            whiteSpace="normal"
            h="auto"
            py={3}
          >
            Only this meeting
          </Button>
          <Button ref={cancelSeriesScopeRef} onClick={onSeriesScopeClose} variant="ghost">
            Cancel
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog
      isOpen={isWeekdayShiftOpen}
      leastDestructiveRef={cancelWeekdayShiftRef}
      onClose={cancelWeekdayShiftDialog}
    >
      <AlertDialogOverlay />
      <AlertDialogContent maxW={{ base: "calc(100vw - 2rem)", md: "xl" }} mx={4}>
        <AlertDialogHeader fontSize="lg" fontWeight="bold">
          {seriesWeekdayPickerHasDateShift
            ? "Which weekdays should move?"
            : "Which weekdays should these updates apply to?"}
        </AlertDialogHeader>
        <AlertDialogBody>
          {seriesWeekdayPickerHasDateShift ? (
            <Text mb={3}>
              You moved this row from <strong>{originalDateRef.current || "—"}</strong> to{" "}
              <strong>{formData.date_ || "—"}</strong> ({dateShiftSummaryText}). The checkboxes list every
              weekday that still has at least one meeting <strong>from today onward</strong> in this series
              (same EHR ID). Select which weekdays should shift by the same calendar delta. Unchecked weekdays
              keep their dates. The shift applies to future meetings on that weekday with dates strictly after
              this row&apos;s original date ({originalDateRef.current || "—"}).
            </Text>
          ) : (
            <Text mb={3}>
              Choose which days of the week should get your changes (time, type, notes behavior, etc.) for{" "}
              <strong>this date and later</strong> in the series. The list is weekdays that have at least one
              meeting <strong>from today onward</strong> (same {getLabel(userRole, 'idField')}). Unchecked weekdays
              are left unchanged.
            </Text>
          )}
          <VStack align="stretch" spacing={2}>
            {weekdaysInSeriesList.map((w) => (
              <Checkbox
                key={w}
                isChecked={selectedWeekdaysForShift.includes(w)}
                onChange={() => toggleWeekdayShift(w)}
                colorScheme="blue"
              >
                <Text as="span" fontSize="sm">
                  {seriesWeekdayPickerHasDateShift ? (
                    <>
                      Move all <strong>{dayNameLabels[w]}</strong> meetings ({seriesWeekdayCounts[w] || 0}{" "}
                      in series) → same shift ({dateShiftSummaryText}; lands on{" "}
                      {dayNameLabels[weekdayAfterCalendarDelta(w, dateShiftDeltaPreview)]})
                    </>
                  ) : (
                    <>
                      Apply changes to all <strong>{dayNameLabels[w]}</strong> meetings ({seriesWeekdayCounts[w] || 0}{" "}
                      in series on or after this occurrence&apos;s date, same series)
                    </>
                  )}
                </Text>
              </Checkbox>
            ))}
          </VStack>
          <Text fontSize="xs" color="gray.600" mt={3}>
            {seriesWeekdayPickerHasDateShift
              ? "Storage paths for reports/audio are not bulk-moved for other dates; only schedule dates update here."
              : "Per-date files (audio, reports) stay on each row unless you change only this meeting."}
          </Text>
        </AlertDialogBody>
        <AlertDialogFooter
          flexDirection={{ base: "column-reverse", sm: "row" }}
          flexWrap="wrap"
          gap={3}
          justifyContent="flex-end"
        >
          <Button ref={cancelWeekdayShiftRef} onClick={cancelWeekdayShiftDialog}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={confirmWeekdayShiftApply}>
            Apply and save
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default EditPatientModal;
